import React, { memo } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import type { SortKey, SortOrder } from '../../hooks/useShiftSort';

/**
 * SortOptions Props
 */
interface SortOptionsProps {
  /** 現在のソートキー */
  sortBy: SortKey;
  /** 現在のソート順序 */
  sortOrder: SortOrder;
  /** ソートキー変更ハンドラー */
  onSortByChange: (key: SortKey) => void;
  /** ソート順序トグルハンドラー */
  onSortOrderToggle: () => void;
}

/**
 * ソートオプションコンポーネント
 *
 * @description
 * REQ-006に基づくソート機能のUIを提供する。
 * 医師名、開始時間でのソートをサポート。
 *
 * @param {SortOptionsProps} props - コンポーネントProps
 * @returns {React.ReactElement} ソートオプション
 *
 * @example
 * <SortOptions
 *   sortBy={sortState.sortBy}
 *   sortOrder={sortState.sortOrder}
 *   onSortByChange={setSortBy}
 *   onSortOrderToggle={toggleSortOrder}
 * />
 */
const SortOptions: React.FC<SortOptionsProps> = memo(
  ({ sortBy, sortOrder, onSortByChange, onSortOrderToggle }) => {
    /**
     * ソートキー変更ハンドラー
     */
    const handleSortByChange = (
      _event: React.MouseEvent<HTMLElement>,
      newValue: SortKey | null
    ) => {
      if (newValue !== null) {
        onSortByChange(newValue);
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* ソートキー選択 */}
        <ToggleButtonGroup
          value={sortBy}
          exclusive
          onChange={handleSortByChange}
          size="small"
          aria-label="ソート項目"
        >
          <ToggleButton value="doctorName" aria-label="医師名でソート">
            <Tooltip title="医師名でソート">
              <PersonIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="startTime" aria-label="開始時間でソート">
            <Tooltip title="開始時間でソート">
              <TimeIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* ソート順序トグル */}
        <Tooltip title={sortOrder === 'asc' ? '昇順' : '降順'}>
          <IconButton
            onClick={onSortOrderToggle}
            size="small"
            color="primary"
            aria-label="ソート順序を切り替え"
          >
            {sortOrder === 'asc' ? <AscIcon /> : <DescIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }
);

SortOptions.displayName = 'SortOptions';

export { SortOptions };
